import type {
    Aggregation,
    Maybe,
    ProductAttributeSortInput
} from '@pylot-data/pylotschema'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useReducer, useRef } from 'react'
import type { SortEnum } from '@pylot-data/enums/SortEnum'
import {
    Action,
    ActionType,
    FiltersStateInput,
    PLPManager,
    PLPState
} from '../FilterTypes'

export enum SearchCriteriaFilterActions {
    EQ = 'EQ',
    FROM = 'FROM',
    TO = 'TO'
}

export type SearchCriteria = {
    attribute_code: string
    filter_action: SearchCriteriaFilterActions
    filter_value: string
}

export type AppliedFilterData = {
    requestVar: string
    value_string: string
    label: string
}

export const serializeSearchCriteria = (criteria: SearchCriteria[]): string => {
    let serializedCriteria = '',
        skipIteration = false
    criteria.forEach((criterion, index) => {
        if (skipIteration) {
            skipIteration = false
            return ''
        }
        const { filter_action, ...criterionWithoutAction } = criterion
        if (criterionWithoutAction.attribute_code == 'price') {
            if (
                filter_action == SearchCriteriaFilterActions.FROM &&
                criteria[index + 1] &&
                criteria[index + 1].filter_action ==
                    SearchCriteriaFilterActions.TO
            ) {
                skipIteration = true
                serializedCriteria += `${criterionWithoutAction.attribute_code}:`
                serializedCriteria += `${criteria[index].filter_value}_${
                    criteria[index + 1].filter_value
                }-`
                return ''
            }
        }
        serializedCriteria += `${Object.values(criterionWithoutAction)
            .map((str) => str.replace(/[^\w]/gi, '_'))
            .join(':')}-`
    })
    // remove the trailing `-` and return the result
    return serializedCriteria.slice(0, serializedCriteria.length - 1)
}

export const parseSearchCriteria = (criteriaString = ''): SearchCriteria[] => {
    const parsedCriteria: SearchCriteria[] = []
    const commaSeparatedCriteria = criteriaString.split('-')
    commaSeparatedCriteria.forEach((csCriterion) => {
        const values = csCriterion.split(':')
        if (values.length === 2)
            if (values[1].includes('_')) {
                const range = values[1].split('_')
                parsedCriteria.push(
                    {
                        attribute_code: values[0],
                        filter_action: SearchCriteriaFilterActions.FROM,
                        filter_value: range[0]
                    },
                    {
                        attribute_code: values[0],
                        filter_action: SearchCriteriaFilterActions.TO,
                        filter_value: range[1]
                    }
                )
            } else {
                parsedCriteria.push({
                    attribute_code: values[0],
                    filter_action: SearchCriteriaFilterActions.EQ,
                    filter_value: values[1]
                })
            }
    })
    return parsedCriteria
}

export const parseAppliedFilterData = (
    criteriaString = ''
): AppliedFilterData[] => {
    const parsedCriteria: AppliedFilterData[] = []
    const commaSeparatedCriteria = criteriaString.split('-')
    commaSeparatedCriteria.forEach((csCriterion) => {
        const values = csCriterion.split(':')
        if (values.length === 2)
            parsedCriteria.push({
                requestVar: values[0],
                label: values[0],
                value_string: values[1]
            })
    })
    return parsedCriteria
}

export const DEFAULT_CRITERIA_PARAMETER_NAME = 'filter'
export const DEFAULT_SORT_PARAMETER_NAME = 'sort'

type SortState = PLPState['sort']

/**
 * Serializa el orden activo para la URL: `{ price_asc: 'ASC' }` → `price_asc_asc`.
 * Devuelve '' cuando no hay orden (el default de la categoría no viaja en la URL).
 */
export const serializeSort = (sort?: SortState | null): string => {
    if (!sort) return ''
    const entry = Object.entries(sort).find(([, direction]) =>
        Boolean(direction)
    )
    if (!entry) return ''
    const [key, direction] = entry
    return `${key}_${String(direction).toLowerCase()}`
}

/** Inverso de `serializeSort`; valores inválidos devuelven `{}`. */
export const parseSort = (value = ''): SortState => {
    const separatorIndex = value.lastIndexOf('_')
    if (separatorIndex <= 0) return {}
    const key = value.slice(0, separatorIndex)
    const direction = value.slice(separatorIndex + 1).toUpperCase()
    if (!/^\w+$/.test(key)) return {}
    if (direction !== 'ASC' && direction !== 'DESC') return {}
    return { [key]: direction as SortEnum } as SortState
}

/**
 * Lee un parámetro de la URL: primero desde `router.query` y, si todavía no está
 * hidratado (primer render en cliente), desde `window.location.search`.
 */
const readUrlParam = (
    query: Record<string, string | string[] | undefined>,
    name: string
): string => {
    const fromQuery = query[name]
    if (fromQuery && typeof fromQuery === 'string' && fromQuery.length > 0) {
        return fromQuery
    }
    if (typeof window !== 'undefined') {
        const params = new URLSearchParams(window.location.search)
        if (params.has(name)) return params.get(name) ?? ''
    }
    return ''
}

/**
 * Reemplaza (o quita) un parámetro en la URL actual sin re-codificar el resto
 * de la query (p. ej. `filter=color:rojo` debe seguir legible).
 */
const buildUrlWithParam = (name: string, value: string): string => {
    const search = window.location.search.replace(/^\?/, '')
    const parts = search
        ? search
              .split('&')
              .filter((part) => part && !part.startsWith(`${name}=`))
        : []
    if (value) parts.push(`${name}=${encodeURIComponent(value)}`)
    return `${window.location.pathname}${parts.length ? `?${parts.join('&')}` : ''}${window.location.hash}`
}

const initialState: PLPState = {
    categoryUid: '',
    aggregations: [],
    filters: {},
    appliedFilterData: [],
    sort: {},
    filtersVisible: false,
    appliedSearchCritieria: []
}

const reducer = (state: PLPState, action: Action): PLPState => {
    switch (action.type) {
        case ActionType.CLEAR_PLP_STATE: {
            const payload = action.payload
            return {
                ...initialState,
                ...payload
            }
        }
        case ActionType.SET_CATEGORY_UID: {
            const payload = action.payload
            return {
                ...state,
                categoryUid: payload
            }
        }
        case ActionType.SET_FILTER: {
            const payload = action.payload as FiltersStateInput
            return {
                ...state,
                filters: payload.filters
                    ? {
                          ...state.filters,
                          ...payload.filters
                      }
                    : {},
                appliedFilterData: payload.appliedFilterData
            }
        }
        case ActionType.SET_AGGREGATIONS: {
            const payload = action.payload as Array<Maybe<Aggregation>>
            return {
                ...state,
                aggregations: payload
            }
        }
        case ActionType.SORT: {
            const payload = action.payload as ProductAttributeSortInput
            return {
                ...state,
                sort: payload
            }
        }
        case ActionType.SET_FILTERS_VISIBLE: {
            const payload = action.payload as boolean
            return {
                ...state,
                filtersVisible: payload
            }
        }
        case ActionType.SET_SEARCH_CRITERIA: {
            const payload = action.payload as SearchCriteria[]
            return {
                ...state,
                appliedSearchCritieria: payload
            }
        }
        default:
            return state
    }
}

export const usePLPManager = (
    args: Partial<PLPState> & {
        disableFilterUrlParams?: boolean
        unfilteredAggregations?: Maybe<Maybe<Aggregation>[]>
        criteriaParameterName?: string
        sortParameterName?: string
    }
): PLPManager => {
    const {
        disableFilterUrlParams = false,
        unfilteredAggregations = initialState.aggregations,
        criteriaParameterName = DEFAULT_CRITERIA_PARAMETER_NAME,
        sortParameterName = DEFAULT_SORT_PARAMETER_NAME,
        ...initialStateOverrides
    } = args

    // Used to get and set searchCriteria in the URL parameters
    const router = useRouter()
    const criteriaParam = router.query[criteriaParameterName]

    const criteriaString =
        criteriaParam &&
        typeof criteriaParam === 'string' &&
        criteriaParam.length > 0
            ? criteriaParam
            : typeof window !== 'undefined' &&
                new URLSearchParams(window.location.search).has(
                    criteriaParameterName
                )
              ? new URLSearchParams(window.location.search).get(
                    criteriaParameterName
                )
              : ''
    // El orden sí viaja en la URL (`?sort=price_asc_asc`) para sobrevivir al
    // volver atrás desde la PDP o a una recarga; el default de la categoría no.
    const sortString = readUrlParam(
        router.query as Record<string, string | string[] | undefined>,
        sortParameterName
    )
    const sortFromUrl = parseSort(sortString)

    const arg = {
        ...initialState,
        ...initialStateOverrides,
        appliedSearchCritieria: parseSearchCriteria(criteriaString as string),
        appliedFilterData: parseAppliedFilterData(criteriaString as string),
        aggregations: unfilteredAggregations,
        sort: Object.keys(sortFromUrl).length
            ? sortFromUrl
            : (initialStateOverrides.sort ?? initialState.sort)
    }

    const [plpState, dispatch] = useReducer(reducer, arg)

    const clearPLPState = useCallback(
        (newPLPState) => {
            dispatch({ type: ActionType.CLEAR_PLP_STATE, payload: newPLPState })
        },
        [dispatch]
    )
    const setCategoryUid = useCallback(
        (categoryUid) => {
            dispatch({
                payload: categoryUid,
                type: ActionType.SET_CATEGORY_UID
            })
        },
        [dispatch]
    )
    const setFilter = useCallback(
        (payload) => {
            dispatch({ payload, type: ActionType.SET_FILTER })
        },
        [dispatch]
    )
    const applySort = useCallback(
        (payload) => {
            if (!disableFilterUrlParams && typeof window !== 'undefined') {
                router.push(
                    buildUrlWithParam(
                        sortParameterName,
                        serializeSort(payload)
                    ),
                    undefined,
                    { shallow: true }
                )
            }
            dispatch({ payload, type: ActionType.SORT })
        },
        [disableFilterUrlParams, router, sortParameterName]
    )
    const setAggregations = useCallback(
        (payload) => {
            dispatch({ payload, type: ActionType.SET_AGGREGATIONS })
        },
        [dispatch]
    )
    const setFiltersVisible = useCallback(
        (payload) => {
            dispatch({ payload, type: ActionType.SET_FILTERS_VISIBLE })
        },
        [dispatch]
    )
    const setSearchCriteria = useCallback(
        (searchCriteriaList) => {
            if (!disableFilterUrlParams && typeof window !== 'undefined') {
                const existingUrl = new URL(window.location.href)
                existingUrl.searchParams.delete(criteriaParameterName)
                const hasOtherParams = existingUrl.search.length > 0
                const hasQuestionMark =
                    searchCriteriaList.length > 0 || hasOtherParams
                const hasAndSign =
                    searchCriteriaList.length > 0 && hasOtherParams

                const newUrlParams =
                    searchCriteriaList.length > 0
                        ? `${criteriaParameterName}=${serializeSearchCriteria(
                              searchCriteriaList
                          )}`
                        : ''

                const newUrl = `${window.location.pathname}${
                    hasQuestionMark ? '?' : ''
                }${newUrlParams}${
                    hasAndSign ? '&' : ''
                }${existingUrl.searchParams.toString()}${window.location.hash}`

                router.push(newUrl, undefined, { shallow: true })
            }
            dispatch({
                payload: searchCriteriaList,
                type: ActionType.SET_SEARCH_CRITERIA
            })
        },
        [criteriaParameterName, disableFilterUrlParams, router]
    )

    useEffect(() => {
        setAggregations(unfilteredAggregations)
    }, [setAggregations, unfilteredAggregations])

    useEffect(() => {
        // `criteriaString` param from the url can be empty on first load - so we need to manually set searchCriteria
        // if we find criteria is set on the second render but searchCriteria is unset
        if (
            router.isReady &&
            criteriaString &&
            plpState.appliedSearchCritieria.length === 0
        ) {
            // setSearchCriteria(parseSearchCriteria(criteriaString as string))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [router.isReady])

    // Navegación atrás/adelante: si el `sort` de la URL cambió, sincronizar el
    // estado. Solo reacciona a cambios reales de la URL para no pisar el orden
    // por defecto que la PLP escribe en memoria cuando no hay parámetro.
    const lastSortStringRef = useRef(sortString)
    useEffect(() => {
        if (lastSortStringRef.current === sortString) return
        lastSortStringRef.current = sortString
        const parsed = parseSort(sortString)
        if (serializeSort(parsed) !== serializeSort(plpState.sort)) {
            dispatch({ payload: parsed, type: ActionType.SORT })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortString])

    useEffect(() => {
        if (
            JSON.stringify(plpState.appliedSearchCritieria) !=
            JSON.stringify(parseSearchCriteria(criteriaString as string))
        ) {
            // setSearchCriteria(parseSearchCriteria(criteriaString as string))
            dispatch({ payload: arg, type: ActionType.SET_FILTER })
        }
    }, [criteriaString])

    return {
        plpState,
        clearPLPState,
        setCategoryUid,
        setFilter,
        setAggregations,
        applySort,
        setFiltersVisible,
        setSearchCriteria
    }
}
